import { CSSProperties, ElementType, forwardRef, KeyboardEvent, ReactNode, SyntheticEvent, useState } from 'react';
import classNames from 'classnames';
import BootstrapDropdown from 'react-bootstrap/Dropdown';
import BootstrapNav from 'react-bootstrap/Nav';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';

import ELEMENT, { createTestId } from 'common/element';
import { MenuItemConfig } from 'common/types';
import { isSafari } from 'service/browser';

import DropdownItems from 'components/common/DropdownItems/DropdownItems';
import Button, { ButtonPropTypes, ButtonRef, ButtonVariant } from 'components/common/Button/Button';

import './Dropdown.scss';

// how long to wait before clearing typed selection text
export const SELECTION_TEXT_TIMEOUT = 750;

/**
 * Dropdown combines a button with a menu. Clicking the button displays the
 * menu and its items.
 *
 * @prop children             the body content of the dropdown toggle
 * @prop sections             list of menu item sections (see DropdownItems)
 * @prop className            class(es) to add to dropdown button
 * @prop variant              variant for dropdown button
 * @prop menuClass            class(es) to add to dropdown menu
 * @prop itemAs*              element or component to use to render each item
 * @prop defaultShow          if true, menu is rendered as open
 * @prop size                 'sm' (default)  | 'med' | 'lg'
 * @prop openInNewTab         if true, open link targets in new tab
 * @prop testId               uniquely identifying string to use for testing (applied to button)
 * @prop alignRight           if true, menu is aligned to the right of the button
 * @prop dropdownClickTrigger action that is performed after user have opened dropdown
 * @prop isNav                if true, this is a navbar dropdown
 */

type DropdownProps = {
  children: ReactNode;
  sections: Array<Array<MenuItemConfig>>;
  className?: string;
  variant?: ButtonVariant;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode | null;
  menuClass?: string;
  itemAs?: ElementType;
  defaultShow?: boolean;
  size?: 'sm' | 'med' | 'lg';
  openInNewTab?: boolean;
  buttonStyle?: CSSProperties;
  testId?: ELEMENT;
  alignRight?: boolean;
  dropdownClickTrigger?: () => void;
  isNav?: boolean;
};

function Dropdown({
  children,
  sections,
  className,
  variant = ButtonVariant.SECONDARY,
  leftIcon,
  rightIcon,
  menuClass,
  itemAs,
  defaultShow,
  size,
  openInNewTab,
  buttonStyle,
  testId,
  alignRight,
  dropdownClickTrigger,
  isNav = false,
}: DropdownProps): JSX.Element {
  const [selectionText, setSelectionText] = useState('');
  const [timerId, setTimerId] = useState(-1);

  // Bootstrap button size defaults to medium; we default to 'sm'
  const bsSize = size === 'med' ? undefined : size || 'sm';

  let toggleIcon = rightIcon;
  if (toggleIcon === undefined) {
    toggleIcon = <ArrowDropDownIcon fontSize="small" />;
  }

  // if user types text, pass it to DropdownItems which will set focus to the first matching item
  const handleKeyUp = (ev: KeyboardEvent) => {
    if (timerId !== -1) {
      window.clearTimeout(timerId);
    }
    const key = ev.key;
    // length of 1 is a reliable enough indicator that we got a printable alpha character
    if (key.length === 1) {
      setSelectionText((text) => text + key);
      setTimerId(window.setTimeout(() => setSelectionText(''), SELECTION_TEXT_TIMEOUT));
    }
  };

  // Safari doesn't transfer browser focus on a non-input click; do it manually so we get key events
  const setFocusSafari = (e: SyntheticEvent): void => {
    if (isSafari) {
      const btn = e.target as HTMLElement;
      setTimeout(() => btn.focus(), 100);
    }
  };

  // to use our Button as the toggle we need to pass its ref to the toggle; we also have to pass through
  // the click handler for some reason (that's based on react-bootstrap docs)
  const ScalyrButton = forwardRef(
    (
      {
        // eslint-disable-next-line no-shadow
        children,
        onClick,
      }: ButtonPropTypes,
      ref: ButtonRef,
    ) => (
      <Button
        variant={isNav ? ButtonVariant.BORDERLESS : variant}
        size={bsSize}
        className={classNames(isNav ? 'nav-link dropdown-toggle' : 'Dropdown__Button', className)}
        leftIcon={leftIcon}
        rightIcon={isNav ? undefined : toggleIcon}
        buttonRef={ref}
        style={buttonStyle}
        testId={testId}
        onClick={(e): void => {
          setFocusSafari(e);
          if (onClick) {
            onClick(e);
          }
          if (dropdownClickTrigger) {
            dropdownClickTrigger();
          }
          e.preventDefault();
        }}
      >
        {children}
      </Button>
    ),
  );

  return (
    <BootstrapDropdown
      show={defaultShow}
      className={isNav ? className : undefined}
      as={isNav ? BootstrapNav.Item : undefined}
      onKeyUp={handleKeyUp}
      onClick={isNav ? (e: SyntheticEvent): void => setFocusSafari(e) : undefined}
      tabIndex={-1}
    >
      <BootstrapDropdown.Toggle
        role="button"
        // @ts-expect-error
        as={isNav ? BootstrapNav.Link : ScalyrButton}
        data-test-id={isNav ? testId : undefined}
      >
        {children}
      </BootstrapDropdown.Toggle>
      <BootstrapDropdown.Menu
        className={classNames('Dropdown__Menu', menuClass)}
        rootCloseEvent="click"
        alignRight={alignRight}
        role="menu"
      >
        <DropdownItems
          sections={sections}
          itemAs={itemAs}
          openInNewTab={openInNewTab}
          selectionText={selectionText}
          testId={createTestId(testId, 'menu')}
        />
      </BootstrapDropdown.Menu>
    </BootstrapDropdown>
  );
}

export default Dropdown;
